import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Download, Eye, CheckCircle, XCircle, Send, FileText, Search, Trash2, Link2, Copy, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgreementsManager() {
  const { admin, getAuthHeader } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [approving, setApproving] = useState(false);
  
  // Filter states
  const [leadIdFilter, setLeadIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/agreements`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setAgreements(data);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyApprovalLink = (agreement) => {
    const link = `${window.location.origin}/public/agreement/${agreement.approval_token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(agreement.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(agreement.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };


  const handleSend = async (agreementId) => {
    if (!window.confirm('Send this agreement to the client via email?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/${agreementId}/send`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Agreement sent successfully');
        fetchAgreements();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send agreement');
      }
    } catch (error) {
      console.error('Error sending agreement:', error);
      alert('Failed to send agreement');
    }
  };

  const handleDeleteAgreement = async (agreementId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this agreement? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/${agreementId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        alert('Agreement deleted successfully');
        fetchAgreements(); // Refresh the list
      } else {
        const error = await response.json();
        alert(`Failed to delete agreement: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting agreement:', error);
      alert('Failed to delete agreement');
    }
  };

  const handleScreenshotUpload = async (file, agreementId) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agreements/${agreementId}/upload-screenshot`,
        {
          method: 'POST',
          headers: getAuthHeader(),
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.screenshot_url;
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    }
    return null;
  };

  const openApprovalModal = (agreementId) => {
    const agreement = agreements.find(a => a.id === agreementId);
    setSelectedAgreement(agreement);
    setShowApprovalModal(true);
    setScreenshot(null);
  };

  const handleApprove = async () => {
    if (!selectedAgreement) return;

    const isAdmin = admin?.role === 'admin' || admin?.role === 'super_admin';

    if (!screenshot && !isAdmin) {
      alert('Please upload a screenshot of the email/proof before approving');
      return;
    }

    setApproving(true);

    try {
      let screenshotUrl = null;
      if (screenshot) {
        screenshotUrl = await handleScreenshotUpload(screenshot, selectedAgreement.id);
        if (!screenshotUrl) {
          alert('Failed to upload screenshot');
          setApproving(false);
          return;
        }
      }

      const approvalMethod = isAdmin ? 'super_admin_override' : 'with_screenshot';

      const response = await fetch(`${BACKEND_URL}/api/agreements/${selectedAgreement.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ 
          approved: true,
          approval_method: approvalMethod,
          screenshot_file: screenshotUrl
        })
      });

      if (response.ok) {
        alert('Agreement approved successfully');
        setShowApprovalModal(false);
        setSelectedAgreement(null);
        setScreenshot(null);
        fetchAgreements();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to approve agreement');
      }
    } catch (error) {
      console.error('Error approving agreement:', error);
      alert('Failed to approve agreement');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/${selectedAgreement.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          approved: false,
          rejection_reason: rejectionReason
        })
      });

      if (response.ok) {
        alert('Agreement rejected successfully');
        setShowRejectionModal(false);
        setSelectedAgreement(null);
        setRejectionReason('');
        fetchAgreements();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reject agreement');
      }
    } catch (error) {
      console.error('Error rejecting agreement:', error);
      alert('Failed to reject agreement');
    }
  };

  const downloadPDF = async (agreementId, agreementNumber) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/${agreementId}/download-pdf`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `agreement_${agreementNumber.replace(/\//g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter agreements
  const filteredAgreements = agreements.filter(agreement => {
    if (leadIdFilter && !agreement.lead_id?.toLowerCase().includes(leadIdFilter.toLowerCase())) {
      return false;
    }
    if (statusFilter && agreement.status !== statusFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading agreements...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Agreements Management</h1>
          <p className="text-gray-600">Manage client agreements and contracts</p>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Lead ID
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Enter Lead ID..."
                  value={leadIdFilter}
                  onChange={(e) => setLeadIdFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setLeadIdFilter('');
                  setStatusFilter('');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {filteredAgreements.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {agreements.length === 0 ? 'No agreements found' : 'No agreements match your filters'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agreement #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAgreements.map(agreement => (
                    <tr key={agreement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-purple-600">
                        {agreement.lead_id}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {agreement.agreement_number}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="text-gray-900 font-medium">{agreement.lead_name}</div>
                        <div className="text-gray-500 text-xs">{agreement.lead_email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="text-gray-900">{agreement.service_type}</div>
                        {agreement.destination_country && (
                          <div className="text-gray-500 text-xs">{agreement.destination_country}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                        ₹{agreement.total_amount ? parseFloat(agreement.total_amount).toFixed(2) : '0.00'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agreement.status)}`}>
                          {agreement.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(agreement.issue_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => downloadPDF(agreement.id, agreement.agreement_number)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Download PDF"
                            data-testid={`download-pdf-${agreement.id}`}
                          >
                            <Download size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedAgreement(agreement);
                              setShowDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                            data-testid={`view-details-${agreement.id}`}
                          >
                            <Eye size={18} />
                          </button>
                          {agreement.status !== 'approved' && agreement.approval_token && (
                            <button
                              onClick={() => handleCopyApprovalLink(agreement)}
                              className={`${copiedId === agreement.id ? 'text-green-600' : 'text-indigo-600 hover:text-indigo-800'}`}
                              title={copiedId === agreement.id ? 'Link Copied!' : 'Copy Approval Link'}
                              data-testid={`copy-link-${agreement.id}`}
                            >
                              {copiedId === agreement.id ? <CheckCircle size={18} /> : <Link2 size={18} />}
                            </button>
                          )}
                          {agreement.status === 'draft' && (
                            <button
                              onClick={() => handleSend(agreement.id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Send to Client via Email"
                              data-testid={`send-email-${agreement.id}`}
                            >
                              <Send size={18} />
                            </button>
                          )}
                          {(agreement.status === 'sent' || agreement.status === 'draft') && (
                            <>
                              <button
                                onClick={() => openApprovalModal(agreement.id)}
                                className="text-green-600 hover:text-green-800"
                                title={agreement.status === 'draft' ? 'Mark as Approved (Admin Override)' : 'Approve Agreement'}
                                data-testid={`approve-${agreement.id}`}
                              >
                                <CheckCircle size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedAgreement(agreement);
                                  setShowRejectionModal(true);
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Reject"
                                data-testid={`reject-${agreement.id}`}
                              >
                                <XCircle size={18} />
                              </button>
                            </>
                          )}
                          {(admin?.role === 'super_admin' || admin?.roles?.includes('super_admin') || 
                            admin?.role === 'admin' || admin?.roles?.includes('admin')) && (
                            <button
                              onClick={() => handleDeleteAgreement(agreement.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Agreement"
                              data-testid={`delete-${agreement.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAgreement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                Agreement {selectedAgreement.agreement_number}
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAgreement(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Lead ID:</span>
                    <span className="ml-2 font-semibold">{selectedAgreement.lead_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-semibold">{selectedAgreement.lead_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2">{selectedAgreement.lead_email}</span>
                  </div>
                  {selectedAgreement.lead_phone && (
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <span className="ml-2">{selectedAgreement.lead_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-3">Agreement Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Type:</span>
                    <span className="font-semibold">{selectedAgreement.service_type}</span>
                  </div>
                  {selectedAgreement.destination_country && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Destination:</span>
                      <span className="font-semibold">{selectedAgreement.destination_country}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-semibold text-lg">₹{selectedAgreement.total_amount ? parseFloat(selectedAgreement.total_amount).toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Issue Date:</span>
                    <span>{selectedAgreement.issue_date ? new Date(selectedAgreement.issue_date).toLocaleDateString() : '-'}</span>
                  </div>
                  {selectedAgreement.validity_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valid Until:</span>
                      <span>{new Date(selectedAgreement.validity_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedAgreement.status)}`}>
                      {selectedAgreement.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {selectedAgreement.rejection_reason && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-800 mb-2">Rejection Reason</h3>
                  <p className="text-sm text-red-700">{selectedAgreement.rejection_reason}</p>
                </div>
              )}

              {selectedAgreement.status !== 'approved' && selectedAgreement.approval_token && (
                <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg" data-testid="approval-link-section">
                  <h3 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                    <Link2 size={16} /> Customer Approval Link
                  </h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    Share this link with the customer via WhatsApp, SMS, or any other channel so they can approve the agreement online.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/public/agreement/${selectedAgreement.approval_token}`}
                      className="flex-1 px-3 py-2 border border-indigo-300 rounded-lg bg-white text-sm text-gray-700"
                      data-testid="approval-link-input"
                    />
                    <button
                      onClick={() => handleCopyApprovalLink(selectedAgreement)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${
                        copiedId === selectedAgreement.id
                          ? 'bg-green-600 text-white'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                      data-testid="copy-approval-link-btn"
                    >
                      {copiedId === selectedAgreement.id ? (
                        <><CheckCircle size={14} /> Copied!</>
                      ) : (
                        <><Copy size={14} /> Copy Link</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedAgreement(null);
                  }}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedAgreement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Reject Agreement</h2>
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setSelectedAgreement(null);
                  setRejectionReason('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Rejecting agreement for <strong>{selectedAgreement.lead_name}</strong>
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleReject}
                  disabled={!rejectionReason.trim()}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Confirm Rejection
                </button>
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setSelectedAgreement(null);
                    setRejectionReason('');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal - with screenshot requirement for non-admin users */}
      {showApprovalModal && selectedAgreement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                Approve Agreement {selectedAgreement.agreement_number}
              </h2>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedAgreement(null);
                  setScreenshot(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Agreement Details</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Lead ID:</span> {selectedAgreement.lead_id}</p>
                  <p><span className="font-medium">Lead:</span> {selectedAgreement.lead_name}</p>
                  <p><span className="font-medium">Email:</span> {selectedAgreement.lead_email}</p>
                  <p><span className="font-medium">Amount:</span> ₹{selectedAgreement.total_amount ? parseFloat(selectedAgreement.total_amount).toFixed(2) : '0.00'}</p>
                  <p><span className="font-medium">Status:</span> {selectedAgreement.status}</p>
                </div>
              </div>

              {selectedAgreement.status !== 'approved' && selectedAgreement.approval_token && (
                <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                    <Link2 size={16} /> Customer Approval Link
                  </h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    Share this link with the customer via WhatsApp, SMS, or any other channel.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/public/agreement/${selectedAgreement.approval_token}`}
                      className="flex-1 px-3 py-2 border border-indigo-300 rounded-lg bg-white text-sm text-gray-700"
                    />
                    <button
                      onClick={() => handleCopyApprovalLink(selectedAgreement)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${
                        copiedId === selectedAgreement.id
                          ? 'bg-green-600 text-white'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {copiedId === selectedAgreement.id ? (
                        <><CheckCircle size={14} /> Copied!</>
                      ) : (
                        <><Copy size={14} /> Copy Link</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {(admin?.role !== 'admin' && admin?.role !== 'super_admin') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Email/Proof Screenshot <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setScreenshot(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="agreement-screenshot-upload"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload a screenshot of the email or proof of customer approval before approving
                  </p>
                </div>
              )}

              {(admin?.role === 'admin' || admin?.role === 'super_admin') && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Admin Override:</strong> As an admin, you can approve this agreement without uploading a screenshot.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50"
                  data-testid="confirm-approve-agreement-btn"
                >
                  {approving ? 'Approving...' : 'Approve Agreement'}
                </button>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedAgreement(null);
                    setScreenshot(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
